import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { OnboardingInstance } from '@hospasense/contracts';
import { useAuth } from '../lib/auth.js';
import { useOnboardingPipeline } from '../lib/queries-s2.js';
import { apiGet, apiSend } from '../lib/api.js';
import { formatDate, relativeTime } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingState,
  Modal,
  PageHeader,
  PermissionDeniedState,
  StatusPill,
  useToast,
} from '../components/ui/index.js';

export interface AccessRequestItem {
  id: string;
  fullName: string;
  email: string;
  phone?: string | null;
  organisationName: string;
  organisationType?: string | null;
  designation?: string | null;
  staffCount?: string | null;
  description?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy?: string | null;
  decisionNote?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function OnboardingPage() {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('onboarding.read');
  const canManage = hasPermission('onboarding.manage');
  const [activeTab, setActiveTab] = useState<'requests' | 'pipeline'>('requests');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>(
    'all',
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<AccessRequestItem | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [decisionNote, setDecisionNote] = useState('');

  const queryClient = useQueryClient();
  const toast = useToast();

  const pipelineQuery = useOnboardingPipeline(allowed);

  const requestsQuery = useQuery({
    queryKey: ['access-requests'],
    queryFn: () => apiGet<{ items: AccessRequestItem[] }>('/api/v1/access-requests'),
    enabled: allowed,
    refetchInterval: 30000,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiSend<AccessRequestItem>('POST', `/api/v1/access-requests/${id}/approve`, { note }),
    onSuccess: (updated) => {
      toast.success(`Approved access for ${updated.fullName} (${updated.organisationName})`);
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      setActionType(null);
      setSelectedRequest(null);
      setDecisionNote('');
    },
    onError: (err: Error) => {
      toast.error(`Approval failed: ${err.message}`);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiSend<AccessRequestItem>('POST', `/api/v1/access-requests/${id}/reject`, { reason }),
    onSuccess: (updated) => {
      toast.success(`Rejected request for ${updated.fullName}`);
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      setActionType(null);
      setSelectedRequest(null);
      setDecisionNote('');
    },
    onError: (err: Error) => {
      toast.error(`Rejection failed: ${err.message}`);
    },
  });

  if (!allowed)
    return <PermissionDeniedState what="the Onboarding and Access Management console" />;

  const allRequests = requestsQuery.data?.items ?? [];
  const pendingCount = allRequests.filter((r) => r.status === 'pending').length;

  const filteredRequests = allRequests.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      return (
        r.fullName.toLowerCase().includes(term) ||
        r.email.toLowerCase().includes(term) ||
        r.organisationName.toLowerCase().includes(term) ||
        (r.phone && r.phone.includes(term))
      );
    }
    return true;
  });

  const pipelineItems = pipelineQuery.data?.items ?? [];
  const inProgressPipeline = pipelineItems.filter((i) => i.status === 'in_progress');
  const finishedPipeline = pipelineItems.filter((i) => i.status !== 'in_progress');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Onboarding & Access Console"
        subtitle="Manage incoming demo requests, review new clinician signups, and track hospital go-live readiness"
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Onboarding & Access' }]} />
        }
      />

      {/* Primary Tab Bar */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('requests')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
            activeTab === 'requests'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          <span>Demo & Access Requests</span>
          {pendingCount > 0 && (
            <span className="rounded-full bg-warning-bg px-2 py-0.5 text-xs font-bold text-warning">
              {pendingCount} pending
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('pipeline')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
            activeTab === 'pipeline'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          <span>Onboarding Pipeline</span>
          <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-bold text-text-muted">
            {pipelineItems.length}
          </span>
        </button>
      </div>

      {/* Tab 1: Access & Demo Requests */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {(['all', 'pending', 'approved', 'rejected'] as const).map((st) => (
                <button
                  key={st}
                  onClick={() => setStatusFilter(st)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    statusFilter === st
                      ? 'bg-primary text-text-inverse'
                      : 'bg-surface border border-border text-text-muted hover:text-text-primary'
                  }`}
                >
                  {st} {st === 'pending' && pendingCount > 0 ? `(${pendingCount})` : ''}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Search by name, email, or hospital…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none sm:w-64"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void requestsQuery.refetch()}
                disabled={requestsQuery.isFetching}
              >
                {requestsQuery.isFetching ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
          </div>

          <Card>
            {requestsQuery.isLoading ? (
              <LoadingState label="Loading access requests…" />
            ) : filteredRequests.length === 0 ? (
              <EmptyState
                title="No access requests found"
                description={
                  searchTerm || statusFilter !== 'all'
                    ? 'Try clearing the search filter.'
                    : 'New demo signups and evaluation requests will appear here.'
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-surface-muted/50 text-xs font-semibold uppercase tracking-wider text-text-muted">
                      <th className="py-3 px-4">Applicant / Role</th>
                      <th className="py-3 px-4">Organization / Type</th>
                      <th className="py-3 px-4">Contact Details</th>
                      <th className="py-3 px-4">Submitted</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {filteredRequests.map((req) => (
                      <tr key={req.id} className="hover:bg-surface-hover/50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-text-primary">{req.fullName}</div>
                          <div className="text-xs text-text-muted">
                            {req.designation || 'Healthcare Professional'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium text-text-primary">
                            {req.organisationName}
                          </div>
                          <div className="text-xs text-text-muted capitalize">
                            {req.organisationType || 'Clinic'} · {req.staffCount || '1-10'} staff
                          </div>
                        </td>
                        <td className="py-3 px-4 text-xs">
                          <div className="text-text-primary">
                            <a href={`mailto:${req.email}`} className="text-link hover:underline">
                              {req.email}
                            </a>
                          </div>
                          {req.phone && (
                            <div className="text-text-muted mt-0.5">
                              <a href={`tel:${req.phone}`} className="hover:text-text-primary">
                                {req.phone}
                              </a>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-xs text-text-muted">
                          <div>{relativeTime(req.createdAt)}</div>
                          <div className="text-[10px] opacity-75">{formatDate(req.createdAt)}</div>
                        </td>
                        <td className="py-3 px-4">
                          {req.status === 'pending' ? (
                            <span className="inline-flex items-center rounded-full bg-warning-bg px-2.5 py-0.5 text-xs font-semibold text-warning">
                              Pending Review
                            </span>
                          ) : req.status === 'approved' ? (
                            <span className="inline-flex items-center rounded-full bg-success-bg px-2.5 py-0.5 text-xs font-semibold text-success">
                              Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-error-bg px-2.5 py-0.5 text-xs font-semibold text-error">
                              Rejected
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {req.status === 'pending' ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  disabled={!canManage}
                                  onClick={() => {
                                    setSelectedRequest(req);
                                    setActionType('approve');
                                    setDecisionNote('Approved for 30-day clinical evaluation');
                                  }}
                                >
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  disabled={!canManage}
                                  onClick={() => {
                                    setSelectedRequest(req);
                                    setActionType('reject');
                                    setDecisionNote('');
                                  }}
                                >
                                  Reject
                                </Button>
                              </>
                            ) : (
                              <div className="text-xs text-text-muted">
                                {req.reviewedBy ? `by ${req.reviewedBy}` : 'Reviewed'}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Tab 2: Onboarding Pipeline */}
      {activeTab === 'pipeline' && (
        <div className="space-y-6">
          {pipelineQuery.isLoading ? (
            <LoadingState label="Loading onboarding pipeline…" />
          ) : pipelineItems.length === 0 ? (
            <Card>
              <EmptyState
                title="No onboarding in progress"
                description="Start onboarding from an organisation's workspace once a contract is signed."
              />
            </Card>
          ) : (
            <>
              <section aria-label="In progress" className="grid gap-4 lg:grid-cols-2">
                {inProgressPipeline.map((instance) => (
                  <PipelineCard key={instance.id} instance={instance} />
                ))}
              </section>
              {finishedPipeline.length > 0 && (
                <section aria-label="Completed or cancelled">
                  <h2 className="mb-2 text-lg font-semibold text-text-primary">History</h2>
                  <div className="grid gap-4 lg:grid-cols-2">
                    {finishedPipeline.map((instance) => (
                      <PipelineCard key={instance.id} instance={instance} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal: Approve Request */}
      {selectedRequest && actionType === 'approve' && (
        <Modal
          open={true}
          title={`Approve Access for ${selectedRequest.fullName}`}
          onClose={() => {
            setSelectedRequest(null);
            setActionType(null);
          }}
        >
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Approving this request authorizes <strong>{selectedRequest.fullName}</strong> from{' '}
              <strong>{selectedRequest.organisationName}</strong> to access the HospaSense
              evaluation environment.
            </p>

            <div>
              <label className="block text-xs font-semibold text-text-primary mb-1">
                Approval Note / Provisioning Plan:
              </label>
              <textarea
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
                className="w-full rounded-md border border-border bg-surface p-2 text-sm text-text-primary focus:border-primary focus:outline-none"
                rows={3}
                placeholder="e.g. Approved for Starter Clinic evaluation (10 doctors, 25 beds)"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setSelectedRequest(null);
                  setActionType(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={approveMutation.isPending}
                onClick={() => {
                  approveMutation.mutate({
                    id: selectedRequest.id,
                    note: decisionNote,
                  });
                }}
              >
                {approveMutation.isPending ? 'Approving…' : 'Confirm & Accept'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Reject Request */}
      {selectedRequest && actionType === 'reject' && (
        <Modal
          open={true}
          title={`Reject Access for ${selectedRequest.fullName}`}
          onClose={() => {
            setSelectedRequest(null);
            setActionType(null);
          }}
        >
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Please provide a reason for declining this request from{' '}
              <strong>{selectedRequest.organisationName}</strong>. This note will be recorded in the
              audit log.
            </p>

            <div>
              <label className="block text-xs font-semibold text-text-primary mb-1">
                Decline Reason / Compliance Note:
              </label>
              <textarea
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
                className="w-full rounded-md border border-border bg-surface p-2 text-sm text-text-primary focus:border-primary focus:outline-none"
                rows={3}
                placeholder="e.g. Identity verification pending or outside eligible healthcare jurisdiction"
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setSelectedRequest(null);
                  setActionType(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={rejectMutation.isPending || !decisionNote.trim()}
                onClick={() => {
                  rejectMutation.mutate({
                    id: selectedRequest.id,
                    reason: decisionNote.trim(),
                  });
                }}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Confirm & Reject'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PipelineCard({ instance }: { instance: OnboardingInstance }) {
  const r = instance.readiness;
  const pct = r.totalSteps === 0 ? 0 : Math.round((r.completedSteps / r.totalSteps) * 100);
  return (
    <Card>
      <CardHeader
        title={
          <Link
            to={`/organisations/${instance.organisationId}?tab=onboarding`}
            className="text-link hover:underline focus-visible:ring-2 focus-visible:ring-ring rounded-sm focus-visible:outline-none"
          >
            {instance.organisationName}
          </Link>
        }
        subtitle={`${instance.templateName} · started ${relativeTime(instance.startedAt)} · target ${formatDate(instance.targetGoLiveDate)}`}
        actions={
          instance.status === 'live' ? (
            <StatusPill tone="success" label="Live" />
          ) : instance.status === 'cancelled' ? (
            <StatusPill tone="neutral" label="Cancelled" />
          ) : r.ready ? (
            <StatusPill tone="success" label="Ready for go-live" />
          ) : (
            <StatusPill tone="warning" label={`${pct}% ready`} />
          )
        }
      />
      <CardBody>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-xs text-text-muted mb-1">
              <span>Go-live readiness</span>
              <span>
                {r.completedSteps} of {r.totalSteps} steps
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
              <div
                className={`h-full transition-all ${r.ready ? 'bg-success' : 'bg-primary'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          {r.blockers.length > 0 && (
            <div className="rounded-md bg-error-bg p-2 text-xs text-error">
              <strong className="font-semibold">Blockers:</strong> {r.blockers.join(', ')}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
